/** @format */

import React, { FC, useEffect, useState } from "react";
import { Descriptions, Drawer, Empty, Skeleton, Space, Tabs, Tag, Timeline, Tooltip } from "antd";
import { DownloadOutlined, EditOutlined, FileTextOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { saveAs } from "file-saver";
import styled from "styled-components";
import {
  TIntermittentAction,
  TIntermittentCommunicationDetail,
  TIntermittentEvent,
  TIntermittentShiftLink,
} from "../../../../redux-modules/declarations/maps/intermittentCommunications";
import {
  apiIntermittentDetail,
  apiIntermittentXml,
  intermittentErrorMessage,
} from "../../../../api/apiIntermittentCommunications";
import { showNotification } from "../../../../utils/showNotification";
import { UIButton } from "../../../ui/Button";
import { UIText } from "../../../ui/Text";
import { UIAlert } from "../../../ui/Alert";
import { BaseColor } from "../../../../theme/maps/Colors";
import {
  ACTION_META,
  ACTION_ORDER,
  DETACH_REASONS,
  EVENT_LABELS,
  fmtDateTime,
  fmtDay,
  fmtShift,
  fullName,
  hoursFromNow,
  inHoursLabel,
  isOvernight,
} from "../constants";
import { StatusTag } from "./StatusTag";
import { WorkerTypeTags } from "./WorkerTypeTags";
import { InpsCodeModal } from "./InpsCodeModal";

interface Props {
  communicationId?: number;
  /** Cambia quando un'azione è stata eseguita: forza il ricaricamento */
  reloadKey: number;
  onClose: () => void;
  onAction: (communication: TIntermittentCommunicationDetail, action: TIntermittentAction) => void;
  /** Dati modificati dal pannello (es. codice INPS): aggiorna elenco e riepilogo */
  onUpdated?: () => void;
  /** Destinatari configurati per gli XML (riepilogo della pagina) */
  xmlRecipients?: string[];
}

export const DetailDrawer: FC<Props> = ({ communicationId, reloadKey, onClose, onAction, onUpdated, xmlRecipients }) => {
  const [detail, setDetail] = useState<TIntermittentCommunicationDetail>();
  const [loading, setLoading] = useState(false);
  const [xmlType, setXmlType] = useState<"send" | "cancel">("send");
  const [xml, setXml] = useState<string>();
  const [xmlLoading, setXmlLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("history");
  const [inpsOpen, setInpsOpen] = useState(false);

  useEffect(() => {
    if (!communicationId) {
      setDetail(undefined);
      return;
    }
    setLoading(true);
    apiIntermittentDetail(communicationId)
      .then(d => {
        setDetail(d);
        setXmlType(d.xml_cancel_filename ? "cancel" : "send");
      })
      .catch(err =>
        showNotification("error", "Comunicazione", intermittentErrorMessage(err, "Impossibile caricare il dettaglio.")),
      )
      .finally(() => setLoading(false));
  }, [communicationId, reloadKey]);

  useEffect(() => {
    if (communicationId) setActiveTab("history");
    setInpsOpen(false);
  }, [communicationId]);

  useEffect(() => {
    if (!detail || activeTab !== "xml") return;
    setXmlLoading(true);
    apiIntermittentXml(detail.id, xmlType)
      .then(setXml)
      .catch(err => showNotification("error", "XML", intermittentErrorMessage(err, "Impossibile caricare l'XML.")))
      .finally(() => setXmlLoading(false));
  }, [detail, xmlType, activeTab]);

  const storedFilename = detail ? (xmlType === "cancel" ? detail.xml_cancel_filename : detail.xml_sent_filename) : null;
  const isPreview = !storedFilename;

  const downloadXml = (): void => {
    if (!xml || !detail) return;
    const name = storedFilename || `ANTEPRIMA_${xmlType === "cancel" ? "ANNULLAMENTO_" : ""}C${detail.id}.xml`;
    saveAs(new Blob([xml], { type: "application/xml;charset=utf-8" }), name);
  };

  const actions = detail ? ACTION_ORDER.filter(a => detail.available_actions.includes(a)) : [];
  const sentTo = detail ? sentRecipients(detail.events) : null;

  return (
    <Drawer
      visible={!!communicationId}
      onClose={onClose}
      width={800}
      destroyOnClose
      title={
        detail ? (
          <Space direction="vertical" size={0}>
            <UIText strong style={{ fontSize: 16 }}>
              {fullName(detail)}
            </UIText>
            <UIText type="secondary" style={{ fontSize: 13 }}>
              {fmtDay(detail.work_date)} · turno {fmtShift(detail)}
              {isOvernight(detail) ? " (+1)" : ""}
            </UIText>
          </Space>
        ) : (
          "Comunicazione"
        )
      }
      extra={detail ? <StatusTag status={detail.status} label={detail.status_label} /> : undefined}
    >
      {loading || !detail ? (
        <Skeleton active />
      ) : (
        <>
          <ContextAlert detail={detail} onEditInps={(): void => setInpsOpen(true)} />

          {actions.length > 0 && (
            <ActionBar>
              {actions.map(a => (
                <UIButton
                  key={a}
                  type={ACTION_META[a].primary ? "primary" : "default"}
                  danger={ACTION_META[a].danger}
                  onClick={(): void => onAction(detail, a)}
                  data-cy={`intermittent-action-${a}`}
                >
                  {ACTION_META[a].label}
                </UIButton>
              ))}
            </ActionBar>
          )}

          <Descriptions
            bordered
            size="small"
            column={2}
            labelStyle={{ whiteSpace: "nowrap", width: 170 }}
            contentStyle={{ whiteSpace: "nowrap" }}
            style={{ marginBottom: 24 }}
          >
            <Descriptions.Item label="Dipendente" span={2} contentStyle={{ whiteSpace: "normal" }}>
              <span style={{ marginRight: 8 }}>{fullName(detail)}</span>
              <WorkerTypeTags types={detail.worker_types} />
              {detail.user?.is_disabled && <Tag>disabilitato</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Codice fiscale">{detail.fiscal_code || "-"}</Descriptions.Item>
            <Descriptions.Item label="Codice INPS">
              {detail.inps_code || <UIText type="danger">mancante</UIText>}
              {detail.inps_code_editable && (
                <Tooltip title={detail.inps_code ? "Modifica il codice in anagrafica" : "Inserisci il codice in anagrafica"}>
                  <UIButton
                    type="link"
                    size="small"
                    icon={detail.inps_code ? <EditOutlined /> : <PlusOutlined />}
                    onClick={(): void => setInpsOpen(true)}
                    style={{ marginLeft: 6, padding: 0, height: "auto" }}
                    data-cy="intermittent-inps-edit"
                  >
                    {detail.inps_code ? "" : "Aggiungi"}
                  </UIButton>
                </Tooltip>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Giorno">{fmtDay(detail.work_date)}</Descriptions.Item>
            <Descriptions.Item label="Turno">
              {fmtShift(detail)}
              {isOvernight(detail) ? " (+1)" : ""}
            </Descriptions.Item>
            <Descriptions.Item label="Sede" span={2} contentStyle={{ whiteSpace: "normal" }}>
              {detail.offices && detail.offices.length ? detail.offices.join(", ") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={detail.sent_at ? "Inviata" : "Invio previsto"}>
              {fmtDateTime(detail.sent_at || detail.send_due_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Controllo presenze">{fmtDateTime(detail.cancel_alert_at)}</Descriptions.Item>
            <Descriptions.Item label={detail.cancelled_at ? "Annullata" : "Annullamento automatico"}>
              {fmtDateTime(detail.cancelled_at || detail.cancel_due_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Limite di legge">{fmtDateTime(detail.legal_cancel_deadline_at)}</Descriptions.Item>
            <Descriptions.Item
              label={sentTo ? "XML inviato a" : "Destinatario XML"}
              span={2}
              contentStyle={{ whiteSpace: "normal" }}
            >
              {(sentTo || xmlRecipients || []).join(", ") || <UIText type="danger">non configurato</UIText>}
            </Descriptions.Item>
            {detail.handled_by && (
              <Descriptions.Item label="Ultima gestione" span={2} contentStyle={{ whiteSpace: "normal" }}>
                {detail.handled_by.surname} {detail.handled_by.name} · {fmtDateTime(detail.handled_at)}
                {detail.note && (
                  <>
                    <br />
                    <UIText type="secondary">{detail.note}</UIText>
                  </>
                )}
              </Descriptions.Item>
            )}
          </Descriptions>

          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="Cronologia" key="history">
              <EventTimeline events={detail.events} />
            </Tabs.TabPane>
            <Tabs.TabPane tab={shiftsTabLabel(detail)} key="shifts">
              <UIText type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 13 }}>
                I turni del giorno coperti da questa comunicazione. Un turno eliminato, spostato o cambiato in
                riposo resta in elenco come «non più valido».
              </UIText>
              {detail.shifts.length === 0 ? (
                <Empty description="Nessun turno collegato" />
              ) : (
                [...detail.shifts]
                  .sort((a, b) => Number(!!a.detached_at) - Number(!!b.detached_at))
                  .map(s => (
                    <ShiftRow key={s.shift_planning_id} detached={!!s.detached_at}>
                      <div>
                        <UIText strong={!s.detached_at} delete={!!s.detached_at}>
                          {shiftTitle(s)}
                        </UIText>
                        {s.office && (
                          <UIText type="secondary" style={{ marginLeft: 8 }}>
                            {s.office}
                          </UIText>
                        )}
                      </div>
                      <Tag color={s.detached_at ? "default" : "green"}>
                        {s.detached_at
                          ? `Non più valido: ${
                              DETACH_REASONS[s.detach_reason || ""] || s.detach_reason || "turno modificato"
                            }`
                          : "Attivo"}
                      </Tag>
                    </ShiftRow>
                  ))
              )}
            </Tabs.TabPane>
            <Tabs.TabPane tab="File XML" key="xml">
              <Space style={{ marginBottom: 12, width: "100%", justifyContent: "space-between" }}>
                <Space>
                  <UIButton
                    type={xmlType === "send" ? "primary" : "default"}
                    ghost={xmlType === "send"}
                    onClick={(): void => setXmlType("send")}
                  >
                    Chiamata
                  </UIButton>
                  <UIButton
                    type={xmlType === "cancel" ? "primary" : "default"}
                    ghost={xmlType === "cancel"}
                    onClick={(): void => setXmlType("cancel")}
                  >
                    Annullamento
                  </UIButton>
                </Space>
                <UIButton icon={<DownloadOutlined />} onClick={downloadXml} disabled={!xml}>
                  Scarica
                </UIButton>
              </Space>
              <Space style={{ marginBottom: 8 }}>
                <FileTextOutlined style={{ color: BaseColor }} />
                <UIText>{storedFilename || "Anteprima: file non ancora inviato"}</UIText>
                {isPreview && (
                  <Tooltip title="Generato dai dati attuali: il file definitivo viene creato al momento dell'invio.">
                    <Tag>anteprima</Tag>
                  </Tooltip>
                )}
              </Space>
              {xmlLoading ? <Skeleton active paragraph={{ rows: 6 }} /> : <XmlBox>{prettyXml(xml || "")}</XmlBox>}
            </Tabs.TabPane>
          </Tabs>
        </>
      )}

      <InpsCodeModal
        communication={detail}
        visible={inpsOpen}
        onCancel={(): void => setInpsOpen(false)}
        onSaved={(updated): void => {
          setInpsOpen(false);
          setDetail(updated);
          if (onUpdated) onUpdated();
        }}
      />
    </Drawer>
  );
};

/** Messaggio in evidenza in base allo stato. */
const ContextAlert: FC<{ detail: TIntermittentCommunicationDetail; onEditInps: () => void }> = ({
  detail,
  onEditInps,
}) => {
  const h = detail.hours_to_auto_cancel ?? hoursFromNow(detail.cancel_due_at);
  const missingInps = !detail.inps_code && detail.inps_code_editable;
  const sendInFuture = moment(detail.send_due_at).isAfter(moment());
  const map: Partial<
    Record<string, { type: "info" | "success" | "warning" | "error"; title: string; text: React.ReactNode }>
  > = {
    scheduled: {
      type: "info",
      title: "In programma",
      text: sendInFuture ? (
        <>
          Partirà in automatico <strong>{inHoursLabel(detail.send_due_at)}</strong> ({fmtDateTime(detail.send_due_at)}).
          Puoi inviarla subito o escluderla.
        </>
      ) : (
        "Partirà in automatico a breve."
      ),
    },
    blocked: {
      type: "error",
      title: "Comunicazione bloccata",
      text: missingInps
        ? `${detail.block_reason || "Dati obbligatori mancanti"}. Inserisci il codice INPS: la comunicazione si sblocca subito.`
        : `${detail.block_reason || "Dati obbligatori mancanti"}. Completa l'anagrafica del dipendente: si sblocca da sola entro 15 minuti.`,
    },
    needs_confirmation: {
      type: "warning",
      title: "Turno fuori programmazione",
      text: `Il turno inizia tra ${hoursFromNow(detail.shift_start_at)} ore, meno delle 24 previste. Conferma per inviare subito la comunicazione.`,
    },
    cancellation_pending: {
      type: "warning",
      title: `Annullamento automatico tra ${h} ${h === 1 ? "ora" : "ore"}`,
      text: `${detail.cancellation_note || "Nessuna timbratura valida"}. L'annullamento partirà ${fmtDateTime(
        detail.cancel_due_at,
      )}. Se il lavoratore era presente, inserisci la presenza oppure usa «Era presente».`,
    },
    expired: {
      type: "error",
      title: "Turno iniziato senza comunicazione",
      text: `La comunicazione non è partita${detail.block_reason ? ` (${detail.block_reason})` : ""}. Se è stata inviata in altro modo, segnala come inviata.`,
    },
    failed: {
      type: "error",
      title: "Invio non riuscito",
      text: detail.last_error || "La mail non è partita dopo 3 tentativi.",
    },
    uncertain: {
      type: "warning",
      title: "Esito dell'invio incerto",
      text: "Il sistema si è interrotto durante l'invio. Controlla nella posta inviata se la mail è partita, poi scegli «Segna come inviata» oppure «Riprova».",
    },
    deadline_missed: {
      type: "error",
      title: "Termine di annullamento superato",
      text: `Il limite di legge era ${fmtDateTime(detail.legal_cancel_deadline_at)}. Decidi se inviare comunque l'annullamento o segnare il lavoratore come presente.`,
    },
    worked: {
      type: "success",
      title: "Presenza verificata",
      text: detail.handled_by
        ? `Segnato come presente da ${detail.handled_by.surname} ${detail.handled_by.name}: nessun annullamento.`
        : "Il lavoratore ha una timbratura valida: nessuna azione necessaria.",
    },
    cancelled: { type: "info", title: "Annullamento inviato", text: `Annullata ${fmtDateTime(detail.cancelled_at)}.` },
  };
  const a = map[detail.status];
  if (!a) return <></>;

  return (
    <UIAlert
      type={a.type}
      showIcon
      message={a.title}
      description={a.text}
      style={{ marginBottom: 16 }}
      action={
        detail.status === "blocked" && missingInps ? (
          <UIButton size="small" type="primary" danger onClick={onEditInps} data-cy="intermittent-inps-add">
            Inserisci codice INPS
          </UIButton>
        ) : undefined
      }
    />
  );
};

const EventTimeline: FC<{ events: TIntermittentEvent[] }> = ({ events }) => {
  if (!events.length) return <Empty description="Nessun evento" />;

  const color = (e: TIntermittentEvent): string => {
    if (["sent", "presence_verified", "unblocked"].includes(e.event)) return "green";
    if (["send_failed", "cancel_failed", "blocked", "expired", "deadline_missed"].includes(e.event)) return "red";
    if (["cancellation_pending", "uncertain", "reminder"].includes(e.event)) return "orange";
    if (["cancelled"].includes(e.event)) return "gray";
    if (["shift_attached", "shift_detached", "send_started", "cancel_started"].includes(e.event)) return "gray";
    return "blue";
  };

  return (
    <Timeline style={{ marginTop: 8 }}>
      {events.map(e => {
        const action = e.payload && typeof e.payload.action === "string" ? e.payload.action : undefined;
        const actionLabel = action && ACTION_META[action as TIntermittentAction]?.label;
        const title = EVENT_LABELS[e.event] || e.event;
        const message = eventMessage(e, title);
        return (
          <Timeline.Item key={e.id} color={color(e)}>
            <UIText strong>
              {title}
              {actionLabel ? `: ${actionLabel}` : ""}
            </UIText>
            <UIText type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              {moment(e.created_at).format("DD/MM/YYYY HH:mm")}
              {e.user ? ` · ${e.user.surname} ${e.user.name}` : " · sistema"}
            </UIText>
            {message && (
              <div>
                <UIText type="secondary">{message}</UIText>
              </div>
            )}
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
};

/** Indirizzi a cui è partito l'ultimo XML (dal registro eventi), se già inviato. */
const sentRecipients = (events: TIntermittentEvent[]): string[] | null => {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if ((e.event === "sent" || e.event === "cancelled") && e.payload && Array.isArray(e.payload.to)) {
      return (e.payload.to as unknown[]).map(String);
    }
  }
  return null;
};

/** "Turni collegati (2)" oppure "Turni collegati (2 + 1 non valido)". */
const shiftsTabLabel = (detail: TIntermittentCommunicationDetail): string => {
  const active = detail.shifts.filter(s => !s.detached_at).length;
  const detached = detail.shifts.length - active;
  return `Turni collegati (${active}${detached ? ` + ${detached} non valid${detached === 1 ? "o" : "i"}` : ""})`;
};

/**
 * Orario del turno; il nome del tipo turno si aggiunge solo se dice qualcosa in più
 * (molti tipi turno si chiamano già "08:00 - 13:30").
 */
const shiftTitle = (s: TIntermittentShiftLink): string => {
  if (!s.shift_type) return "Turno";
  const range = `${s.shift_type.time_from.slice(0, 5)} – ${s.shift_type.time_to.slice(0, 5)}`;
  const name = (s.shift_type.name || "").trim();
  const digits = (v: string): string => v.replace(/\D/g, "");
  const nameIsTime = !name || digits(name) === digits(range) || /^\d{1,2}[:.]\d{2}\s*[-–]\s*\d{1,2}[:.]\d{2}$/.test(name);
  return nameIsTime ? range : `${name} · ${range}`;
};

/** Testo sotto il titolo dell'evento: niente doppioni, motivi tecnici tradotti. */
const eventMessage = (e: TIntermittentEvent, title: string): string | null => {
  const shiftId = e.payload && e.payload.shift_planning_id ? `Turno #${e.payload.shift_planning_id}` : null;
  if (e.event === "shift_detached") {
    const reason = (e.message || "").split(": ")[1];
    return [shiftId, reason ? DETACH_REASONS[reason] || reason : null].filter(Boolean).join(" · ") || null;
  }
  if (e.event === "shift_attached") return shiftId;
  if (e.event === "send_failed" || e.event === "cancel_failed") {
    const attempt = e.payload && e.payload.attempt ? `Tentativo ${e.payload.attempt}: ` : "";
    return attempt + (e.message || "");
  }
  if (!e.message || e.message.trim().toLowerCase() === title.toLowerCase()) return null;
  return e.message;
};

/** Un elemento per riga, per leggere l'XML (il file inviato resta quello originale). */
const prettyXml = (xml: string): string => xml.replace(/\n>/g, ">").replace(/\n\/>/g, "/>").replace(/></g, ">\n<");

const ActionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const ShiftRow = styled.div<{ detached: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid #f0f0f0;
  border-radius: 7px;
  background: ${(p): string => (p.detached ? "#fafafa" : "white")};
`;

const XmlBox = styled.pre`
  max-height: 360px;
  overflow: auto;
  margin: 0;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 7px;
  font-family: Inconsolata, Menlo, monospace;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre;
`;
